import { type Currency, capitalizeTerm, formatAmount, parseAmount } from "@loot-os/api-contract";
import { type RefCallback, useState } from "react";

import { boxSearchFocusRef } from "../../../../lib/boxSearchFocus.js";
import { useCurrency } from "../../../../lib/currency.js";
import { useShopper } from "../../../../lib/shopper.js";
import { useTerminology } from "../../../../lib/terminology.js";
import { type BoxSort, useBoxesSearch } from "./search.js";

export interface TextField {
  value: string;
  /** The field's accessible name. */
  label: string;
  placeholder: string;
  onChange: (text: string) => void;
}

export interface AmountField extends TextField {
  /** What was typed does not read as an amount, so it filters nothing. */
  invalid: boolean;
}

export interface BoxFiltersContent {
  /** `focusRef` goes on the search input, which the header's search button focuses. */
  search: TextField & { focusRef: RefCallback<HTMLInputElement> };
  price: { currency: Currency; min: AmountField; max: AmountField };
  sort: {
    value: BoxSort;
    label: string;
    options: Array<{ value: BoxSort; label: string }>;
    onValueChange: (sort: BoxSort) => void;
  };
  /** Only boxes the shopper's balance covers; null signed out, with no balance to compare. */
  affordable: {
    checked: boolean;
    label: string;
    onCheckedChange: (checked: boolean) => void;
  } | null;
  clear: { label: string; onClear: () => void };
  /** How many filters are set, so a layout that hides them (a phone's dialog) can say so. */
  activeCount: number;
}

const SORT_LABELS: Record<BoxSort, string> = {
  default: "Default",
  "price-asc": "Price: low to high",
  "price-desc": "Price: high to low",
  popular: "Most opened",
};

/**
 * A field over a URL value: it shows what the shopper typed and writes what that parses to, and
 * starts over when the URL moves on its own (Clear, back and forward) rather than echoing an edit.
 */
function useUrlDraft<T>(
  committed: T | undefined,
  format: (value: T) => string,
  parse: (text: string) => T | undefined,
  commit: (value: T | undefined) => void,
) {
  const toText = (value: T | undefined) => (value === undefined ? "" : format(value));
  const [draft, setDraft] = useState(() => ({ text: toText(committed), committed }));
  let text = draft.text;
  if (draft.committed !== committed) {
    text = parse(draft.text) === committed ? draft.text : toText(committed);
    setDraft({ text, committed });
  }
  return {
    text,
    onChange: (next: string) => {
      setDraft((current) => ({ ...current, text: next }));
      commit(parse(next));
    },
  };
}

/** The search, price, sort and affordability filters, all of which live in the URL. */
export function useBoxFiltersContent(): BoxFiltersContent {
  const { box } = useTerminology();
  const currency = useCurrency();
  const { user } = useShopper();
  const [search, update] = useBoxesSearch();

  const parseText = (text: string) => (text === "" ? undefined : text);
  const parseCents = (text: string) => {
    if (text.trim() === "") return undefined;
    const cents = parseAmount(text, currency);
    return cents === null || cents < 0 ? undefined : cents;
  };
  // Bare, like what is typed: the field's addon carries the symbol.
  const formatCents = (cents: number) =>
    formatAmount(cents, { symbol: "", multiplier: currency.multiplier });

  const query = useUrlDraft(
    search.q,
    (q) => q,
    parseText,
    (q) => update({ q }),
  );
  const min = useUrlDraft(search.min, formatCents, parseCents, (cents) => update({ min: cents }));
  const max = useUrlDraft(search.max, formatCents, parseCents, (cents) => update({ max: cents }));
  const amountField = (
    field: { text: string; onChange: (text: string) => void },
    label: string,
    placeholder: string,
  ): AmountField => ({
    value: field.text,
    label,
    placeholder,
    onChange: field.onChange,
    invalid: field.text.trim() !== "" && parseCents(field.text) === undefined,
  });

  return {
    search: {
      value: query.text,
      label: `Search ${box.plural}`,
      placeholder: `Search ${capitalizeTerm(box.plural)}`,
      onChange: query.onChange,
      focusRef: boxSearchFocusRef,
    },
    price: {
      currency,
      min: amountField(min, "Minimum price", "Min"),
      max: amountField(max, "Maximum price", "Max"),
    },
    sort: {
      value: search.sort ?? "default",
      label: "Sort",
      options: (Object.keys(SORT_LABELS) as BoxSort[]).map((value) => ({
        value,
        label: SORT_LABELS[value],
      })),
      onValueChange: (sort) => update({ sort: sort === "default" ? undefined : sort }),
    },
    affordable: user
      ? {
          checked: search.enough === true,
          label: `Enough ${currency.namePlural || currency.name}`,
          onCheckedChange: (checked) => update({ enough: checked ? true : undefined }),
        }
      : null,
    activeCount: [
      search.q?.trim(),
      search.min !== undefined,
      search.max !== undefined,
      search.sort !== undefined,
      search.enough,
    ].filter(Boolean).length,
    clear: {
      label: "Clear",
      onClear: () =>
        update({
          q: undefined,
          min: undefined,
          max: undefined,
          sort: undefined,
          enough: undefined,
        }),
    },
  };
}
